import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getProductoById } from "../services/productoService";
import { formatNumber } from "../utils/numberFormat";
import { buildProductCostSections } from "../utils/productoDetail";

const margenIzquierdo = 15;
const margenDerecho = 195;

const cargarLogoShastino = async () => {
    const response = await fetch(`${process.env.PUBLIC_URL}/shastino-logo.png`);
    if (!response.ok) {
        throw new Error(`No se pudo cargar el logo de Shastino (${response.status})`);
    }

    const logoBlob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
                resolve(reader.result);
            } else {
                reject(new Error("El logo de Shastino no se pudo convertir para el PDF"));
            }
        };
        reader.onerror = () => reject(reader.error || new Error("Error al leer el logo de Shastino"));
        reader.readAsDataURL(logoBlob);
    });
};

const dibujarEncabezado = (doc, logo, fecha) => {
    const anchoPagina = doc.internal.pageSize.getWidth();
    doc.setFillColor(17, 42, 68);
    doc.rect(0, 0, anchoPagina, 38, "F");
    doc.setFillColor(231, 76, 60);
    doc.rect(0, 38, anchoPagina, 2, "F");

    if (logo) {
        doc.addImage(logo, "PNG", 15, 7, 58, 20);
    } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(255, 255, 255);
        doc.text("SHASTINO", 15, 20);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text("DETALLE DEL PRODUCTO", anchoPagina - 15, 17, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Fecha: ${fecha}`, anchoPagina - 15, 25, { align: "right" });
    doc.setTextColor(0, 0, 0);
};

const dibujarPie = (doc) => {
    const altoPagina = doc.internal.pageSize.getHeight();
    doc.setDrawColor(205, 213, 222);
    doc.line(margenIzquierdo, altoPagina - 22, margenDerecho, altoPagina - 22);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(70, 80, 90);
    doc.text("Instagram: @shastino_equipamientos  |  WhatsApp: 11 6245-7151", margenIzquierdo, altoPagina - 15);
    doc.text("Dardo Rocha 4362, El Jagüel  |  www.shastino.com.ar", margenIzquierdo, altoPagina - 9);
    doc.setTextColor(0, 0, 0);
};

const formatoMoneda = (valor) => `$${formatNumber(valor ?? 0)}`;

export default function ProductoDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [producto, setProducto] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await getProductoById(id);
                setProducto(data);
            } catch (error) {
                console.error("Error al cargar producto:", error);
                alert("Error al cargar producto");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const descargarPDF = async () => {
        if (!producto) {
            alert("No hay datos del producto para descargar");
            return;
        }

        try {
            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const ahora = new Date();
            const fecha = ahora.toLocaleDateString("es-AR");
            const nombreSeguro = (producto.nombre || "producto")
                .toString()
                .replace(/[^a-zA-Z0-9.-]+/g, "_");
            const fechaCreacion = producto.fechaCreacion
                ? new Date(producto.fechaCreacion).toLocaleDateString("es-AR")
                : "No especificada";
            const secciones = buildProductCostSections(producto);
            let logo;

            try {
                logo = await cargarLogoShastino();
            } catch (error) {
                console.warn("Se generará el PDF sin el logo:", error);
            }

            dibujarEncabezado(doc, logo, fecha);

            let y = 51;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(15);
            doc.setTextColor(17, 42, 68);
            const nombreLineas = doc.splitTextToSize(
                producto.nombre || "Producto",
                margenDerecho - margenIzquierdo
            );
            doc.text(nombreLineas, margenIzquierdo, y);
            y += nombreLineas.length * 7 + 3;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(70, 80, 90);
            doc.text(`Fecha de creación: ${fechaCreacion}`, margenIzquierdo, y);
            y += 7;

            const anchoResumen = margenDerecho - margenIzquierdo;
            const anchoColumna = anchoResumen / 3;
            doc.setFillColor(243, 247, 251);
            doc.setDrawColor(220, 228, 236);
            doc.roundedRect(margenIzquierdo, y, anchoResumen, 22, 2, 2, "FD");

            const resumen = [
                { label: "COSTO TOTAL", value: formatoMoneda(producto.costoTotal) },
                { label: "GANANCIA ESTABLECIDA", value: `${formatNumber(producto.porcentajeGanancia ?? 0)}%` },
                { label: "GANANCIA EN PLATA", value: formatoMoneda(producto.gananciaGenerada) },
            ];
            resumen.forEach((dato, index) => {
                const x = margenIzquierdo + index * anchoColumna + 5;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(7);
                doc.setTextColor(90, 105, 120);
                doc.text(dato.label, x, y + 8);
                doc.setFontSize(11);
                doc.setTextColor(17, 42, 68);
                doc.text(dato.value, x, y + 16);
            });
            y += 31;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(17, 42, 68);
            doc.text("DETALLE DE COMPONENTES E ÍTEMS", margenIzquierdo, y);
            y += 4;

            const cuerpoTabla = [];
            secciones.forEach((seccion) => {
                cuerpoTabla.push([
                    { content: seccion.nombre, colSpan: 3 },
                    { content: formatoMoneda(seccion.total), styles: { halign: "right" } },
                ]);
                seccion.items.forEach((item) => {
                    cuerpoTabla.push([
                        item.nombre,
                        formatNumber(item.cantidad),
                        formatoMoneda(item.costoUnitario),
                        formatoMoneda(item.subtotal),
                    ]);
                });
                cuerpoTabla.push([
                    { content: `Subtotal ${seccion.nombre}`, colSpan: 3, styles: { halign: "right" } },
                    { content: formatoMoneda(seccion.total), styles: { halign: "right" } },
                ]);
            });

            if (cuerpoTabla.length === 0) {
                cuerpoTabla.push([
                    { content: "No hay componentes ni ítems para mostrar.", colSpan: 4 },
                ]);
            }

            doc.autoTable({
                startY: y,
                head: [["Ítem o componente", "Cant.", "Costo unitario", "Subtotal"]],
                body: cuerpoTabla,
                theme: "striped",
                margin: { left: margenIzquierdo, right: margenIzquierdo, top: 48, bottom: 27 },
                headStyles: {
                    fillColor: [17, 42, 68],
                    textColor: 255,
                    fontSize: 8,
                    fontStyle: "bold",
                },
                bodyStyles: { fontSize: 8, textColor: [50, 60, 70] },
                alternateRowStyles: { fillColor: [247, 249, 251] },
                columnStyles: {
                    0: { cellWidth: 83 },
                    1: { cellWidth: 18, halign: "right" },
                    2: { cellWidth: 37, halign: "right" },
                    3: { cellWidth: 42, halign: "right" },
                },
                didParseCell: (data) => {
                    if (data.section === "body" && (data.row.raw[0]?.colSpan === 3)) {
                        data.cell.styles.fillColor = [231, 238, 245];
                        data.cell.styles.textColor = [17, 42, 68];
                        data.cell.styles.fontStyle = "bold";
                    }
                    if (data.section === "body" && data.row.raw[0]?.content?.startsWith("Subtotal ")) {
                        data.cell.styles.fillColor = [243, 247, 251];
                        data.cell.styles.fontStyle = "bold";
                    }
                },
                didDrawPage: () => {
                    dibujarEncabezado(doc, logo, fecha);
                    dibujarPie(doc);
                },
            });

            doc.save(`producto_${nombreSeguro}_${fecha.replace(/\//g, "-")}.pdf`);
        } catch (error) {
            console.error("Error al generar el PDF del producto:", error);
            alert("No se pudo generar el PDF. Intenta nuevamente.");
        }
    };

    if (loading) return <p className="p-4">Cargando...</p>;
    if (!producto) return <p className="p-4">Producto no encontrado</p>;

    const costoTotal = producto.costoTotal ?? 0;
    const gananciaGenerada = producto.gananciaGenerada ?? 0;
    const porcentajeGanancia = producto.porcentajeGanancia ?? 0;
    const fechaCreacion = producto.fechaCreacion
        ? new Date(producto.fechaCreacion).toLocaleDateString("es-AR")
        : "N/A";
    const secciones = buildProductCostSections(producto);

    const containerStyle = {
        maxWidth: 1000,
        width: "95%",
        margin: "0 auto",
        padding: 24,
        background: "#fff",
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        borderRadius: 8,
        color: "#222",
    };

    const metaStyle = { lineHeight: 1.6, color: "#333" };

    return (
        <div style={containerStyle}>
            <div className="flex justify-between items-center mb-4">
                <button
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded"
                    onClick={() => navigate(-1)}
                >
                    ← Volver
                </button>
                <button
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold transition-colors"
                    onClick={descargarPDF}
                    title="Descargar detalle del producto como PDF"
                >
                    📄 Descargar PDF
                </button>
            </div>

            <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>{producto.nombre}</h1>
            <div style={{ marginBottom: 18 }}>
                <div style={metaStyle}><strong>Fecha de creación:</strong> {fechaCreacion}</div>
                <div style={metaStyle}><strong>Costo total:</strong> ${formatNumber(costoTotal)}</div>
                <div style={metaStyle}><strong>Ganancia establecida:</strong> {porcentajeGanancia}%</div>
                <div style={metaStyle}><strong>Ganancia en plata:</strong> ${formatNumber(gananciaGenerada)}</div>
            </div>

            <h3 className="text-lg font-semibold mb-2">Componentes e ítems que lo componen</h3>
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #eee" }}>Ítem o componente</th>
                            <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "2px solid #eee" }}>Cantidad</th>
                            <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "2px solid #eee" }}>Costo unitario</th>
                            <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "2px solid #eee" }}>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {secciones.length === 0 ? (
                            <tr>
                                <td style={{ padding: "18px 12px" }} colSpan={4}>No hay componentes ni ítems para mostrar.</td>
                            </tr>
                        ) : secciones.map((seccion, seccionIndex) => (
                            <Fragment key={`section-${seccionIndex}`}>
                                <tr key={`section-${seccionIndex}`} style={{ background: "#e7eef5", color: "#112a44", fontWeight: 700 }}>
                                    <td style={{ padding: "10px 12px" }}>{seccion.nombre}</td>
                                    <td />
                                    <td />
                                    <td style={{ padding: "10px 12px", textAlign: "right" }}>Total: ${formatNumber(seccion.total)}</td>
                                </tr>
                                {seccion.items.map((item, itemIndex) => (
                                    <tr
                                        key={`item-${seccionIndex}-${itemIndex}`}
                                        style={{ background: itemIndex % 2 === 0 ? "#fff" : "#fafafa" }}
                                    >
                                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0" }}>{item.nombre}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>{formatNumber(item.cantidad)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>${formatNumber(item.costoUnitario)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>${formatNumber(item.subtotal)}</td>
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
